import { FC, useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import ErrorIcon from '@mui/icons-material/Error';
import { useTheme } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import { client, StoreLevelMOQ, StoreLevelMOQFilterOptions } from '../../../app/services/api/orderManagementClient';
import Pagination from '../../../components/forms/Pagination';
import useLogError from '../../../hooks/useLogError';

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

type EditableRow = StoreLevelMOQ & {
  _hasError?: boolean;
  _isNew?: boolean;
  _isDirty?: boolean;
};

const toDateValue = (value?: string | null): Dayjs | null => {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

type Props = {
  companyId: number;
  customerTypeId: number;
  itemNumber: string;
};

const PAGE_SIZE = 10;

const StoreLevelMOQGrid: FC<Props> = ({ companyId, customerTypeId, itemNumber }) => {
  const { t } = useTranslation('productManagement');
  const { logError } = useLogError();

  const { palette } = useTheme();
  const [rows, setRows] = useState<EditableRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [saveStatuses, setSaveStatuses] = useState<Record<number, SaveStatus>>({});

  const rowsRef = useRef<EditableRow[]>([]);
  const pendingBlurRef = useRef<{ index: number; timeout: ReturnType<typeof setTimeout> } | null>(null);

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  const loadData = useCallback(async () => {
    if (!companyId || !customerTypeId || !itemNumber) return;
    setIsLoading(true);
    try {
      const response = await client.getStoreLevelMOQs({
        companyId: companyId.toString(),
        customerTypeId: customerTypeId.toString(),
        itemNumber,
        searchTerm,
        pageNumber: currentPage,
        pageSize: PAGE_SIZE,
      } as StoreLevelMOQFilterOptions);
      if (response.success && response.data) {
        setRows(response.data.items || []);
        setTotalPages(response.data.totalPages || 0);
      }
    } catch (error) {
      logError('Failed to load store level MOQs');
    } finally {
      setIsLoading(false);
    }
  }, [companyId, customerTypeId, itemNumber, currentPage, searchTerm]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const isRowValuesEmpty = (row: EditableRow): boolean =>
    (!row.quantity || row.quantity === 0) && !row.startDate && !row.endDate;

  const isRowComplete = (row: EditableRow): boolean =>
    !!row.customerNumber && !!row.quantityType && !!row.quantity && !!row.startDate && !!row.endDate;

  const setSaveStatus = (index: number, status: SaveStatus) => {
    setSaveStatuses(prev => ({ ...prev, [index]: status }));
  };

  const handleRowSave = useCallback(async (index: number) => {
    const row = rowsRef.current[index];

    if (!row) return;
    if (!row._isDirty) return;
    if (!row.id && (!row.quantity || row.quantity === 0) && !row.startDate && !row.endDate) return;

    const empty = isRowValuesEmpty(row);
    const complete = isRowComplete(row);

    if (!empty && !complete) {
      setRows(prev => {
        const updated = [...prev];
        if (updated[index]) updated[index] = { ...updated[index], _hasError: true };
        return updated;
      });
      return;
    }

    if (row._isNew && empty) {
      setRows(prev => prev.filter((_, i) => i !== index));
      return;
    }

    setSaveStatus(index, 'saving');
    try {
      const payload: StoreLevelMOQ = {
        id: row.id,
        companyId: row.companyId,
        customerNumber: row.customerNumber,
        productCode: row.productCode,
        quantityType: row.quantityType,
        quantity: empty ? 0 : row.quantity,
        startDate: row.startDate,
        endDate: row.endDate,
      };
      const result = await client.updateStoreLevelMOQ(payload);
      setRows(prev => {
        const updated = [...prev];
        if (updated[index]) {
          if (result.data === 0) {
            // backend deleted — reset row to empty, clear id
            updated[index] = { ...updated[index], id: 0, quantity: null, startDate: '', endDate: '', _isDirty: false, _isNew: true };
          } else {
            updated[index] = { ...updated[index], id: result.data, _isDirty: false, _isNew: false };
          }
        }
        return updated;
      });
      setSaveStatus(index, 'saved');
      setTimeout(() => setSaveStatus(index, 'idle'), 2000);
    } catch (error) {
      logError('Failed to save store level MOQ');
      setSaveStatus(index, 'error');
      setRows(prev => {
        const updated = [...prev];
        if (updated[index]) updated[index] = { ...updated[index], _hasError: true };
        return updated;
      });
    }
  }, []);

  const handleCellFocus = (index: number) => {
    if (pendingBlurRef.current?.index === index) {
      clearTimeout(pendingBlurRef.current.timeout);
      pendingBlurRef.current = null;
    }
  };

  const handleCellBlur = (index: number) => {
    if (pendingBlurRef.current) {
      clearTimeout(pendingBlurRef.current.timeout);
    }
    const timeout = setTimeout(async () => {
      pendingBlurRef.current = null;
      await handleRowSave(index);
    }, 200);
    pendingBlurRef.current = { index, timeout };
  };

  const handleCellChange = (index: number, field: keyof StoreLevelMOQ, value: unknown) => {
    setRows(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value, _hasError: false, _isDirty: true };
      return updated;
    });
  };

  const handleSearch = () => {
    setCurrentPage(0);
    setSearchTerm(searchInput);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="textMD"
          fontWeight={600}
          sx={{ m: 0 }}
        >
          {t('storeQuantityLimits')}
        </Typography>
        <Grid container 
          mt={2}
          spacing={2} 
          alignItems="center">
          <Grid item>
            <TextField
              label={t('search')}
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              size="small"
              sx={{ minWidth: 240 }}
            />
          </Grid>
          <Grid item
            sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              aria-label={t('search')}
              size='lg'
              sx={{ mb: 6 }}
              onClick={handleSearch}>{t('search')}</Button>
          </Grid>
          <Grid item
            sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              aria-label={t('clear')}
              size='lg'
              variant='secondary'
              sx={{ mb: 6 }}
              onClick={() => {
                setSearchInput('');
                setCurrentPage(0);
                setSearchTerm('');
              }}>
              {t('clear')}</Button>
          </Grid>
        </Grid>
      </Box>

      {isLoading ? (
        <Box
          sx={{ display: 'flex', justifyContent: 'center', p: 4 }}
        >
          <CircularProgress size={24} />
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ py: 0,  fontSize: '0.8rem', width: 350 }}>{t('storeNumber')}</TableCell>
                  <TableCell sx={{ py: 0, fontSize: '0.8rem', width: 120, textAlign: 'center' }}>{t('quantityType')}</TableCell>
                  <TableCell sx={{ py: 0, fontSize: '0.8rem', width: 100, textAlign: 'center' }}>{t('quantity')}</TableCell>
                  <TableCell sx={{ py: 0, fontSize: '0.8rem', width: 270, textAlign: 'center' }}>{t('startDate')}</TableCell>
                  <TableCell sx={{ py: 0, fontSize: '0.8rem', width: 270, textAlign: 'center' }}>{t('endDate')}</TableCell>
                  <TableCell sx={{ py: 0, width: 60 }} />                
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      align="center"
                    >
                      <Typography color="text.secondary"
                      >
                        {t('noStoreMOQs')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((row, index) => (
                  <TableRow
                    key={index}
                  >
                    <TableCell sx={{ py: 0, fontSize: '0.8rem', width: 350 }}>
                      <Typography sx={{ fontSize: '0.8rem' }}>
                        {`${row.customerNumber ?? ''} ${row.storeName || ''}`}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 0, fontSize: '0.8rem', width: 120 }}>
                      <Typography sx={{ fontSize: '0.8rem', textAlign: 'center' }}>
                        {row.quantityType === '1' ? t('min') : t('max')}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ pt: '16px', textAlign: 'center' }}>
                      <TextField
                        type="number"
                        value={row.quantity || ''}
                        size="small"
                        error={row._hasError}
                        onChange={e => handleCellChange(
                          index,
                          'quantity',
                          e.target.value === '' ? null : Number(e.target.value),
                        )}
                        onFocus={() => handleCellFocus(index)}
                        onBlur={() => handleCellBlur(index)}
                        sx={{ width: 100 }}
                        inputProps={{ style: { textAlign: 'center' } }}
                      />
                    </TableCell>
                    <TableCell sx={{ pt: '16px', textAlign: 'center' }}>
                      <DatePicker
                        value={toDateValue(row.startDate)}
                        onChange={newValue => {
                          handleCellChange(index, 'startDate', newValue ? newValue.format('YYYY-MM-DD') : '');
                        }}
                        onClose={() => handleCellBlur(index)}
                        slotProps={{
                          textField: {
                            size: 'small',
                            error: row._hasError,
                            onFocus: () => handleCellFocus(index),
                            onBlur: () => handleCellBlur(index),
                            sx: { width: 270 },
                          },
                          field: { clearable: true },
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ pt: '16px', textAlign: 'center' }}>
                      <DatePicker
                        value={toDateValue(row.endDate)}
                        onChange={newValue => {
                          handleCellChange(index, 'endDate', newValue ? newValue.format('YYYY-MM-DD') : '');
                        }}
                        onClose={() => handleCellBlur(index)}
                        slotProps={{
                          textField: {
                            size: 'small',
                            error: row._hasError,
                            onFocus: () => handleCellFocus(index),
                            onBlur: () => handleCellBlur(index),
                            sx: { width: 270 },
                          },
                          field: { clearable: true },
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 0, width: 60 }}>
                      {saveStatuses[index] === 'saving' && <CircularProgress size={ 22 } />}
                      {saveStatuses[index] === 'saved' && <CloudDoneIcon sx={{ fontSize: 22, color: palette.success.main }} />}
                      {saveStatuses[index] === 'error' && <ErrorIcon sx={{ fontSize: 22, color: palette.error.main }} />}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Pagination
              page={currentPage}
              setPage={setCurrentPage}
              maxPages={totalPages}
              breakpointForChangeDisplay={120000}
              useNoDataMessage={false}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default StoreLevelMOQGrid;
